import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Card, TextField, InputAdornment, Typography, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  Button, Select, MenuItem, FormControl, InputLabel, Stack,
  ToggleButton, ToggleButtonGroup, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { ComboPartners } from "../components/combos/index.js";
import { useApi } from "../hooks/useApi.js";
import { ui } from "../store/uiStore.js";
import { formLabel } from "../lib/labels.js";
import { currentPeriod, type Debit, type Partner } from "@kappa-v2/contracts";
import { format } from "date-fns";

const CURRENT_YEAR = new Date().getFullYear();
// Periodi "MM/YYYY" dal più recente (default) ai precedenti.
const PERIODS: string[] = [];
for (let y = CURRENT_YEAR + 1; y >= 2024; y--) {
  for (let m = 12; m >= 1; m--) PERIODS.push(`${String(m).padStart(2, "0")}/${y}`);
}
const YEARS = Array.from({ length: CURRENT_YEAR + 1 - 2024 + 1 }, (_, i) => `${CURRENT_YEAR + 1 - i}`);

type FilterMode = "period" | "year" | "custom";

// NB: pagina adattata in Fase 1 ai contracts allineati alla produzione
// (debit/datePaid/paymentType/paymentNumber/period MM/YYYY). Rework UX completo in Fase 5
// (combo soci, filtri storico avanzati, navigazione per-socio).

// Chiavi i18n dei metodi di pagamento (come in produzione: form.payment.*)
const PAYMENT_TYPES = [
  "form.payment.cash",
  "form.payment.bankTransfer",
  "form.payment.pos",
  "form.payment.electronicPayment"
];

interface FormValues {
  _id?: string;
  personId: string; debit: number; period: string;
  datePaid: string; paymentType: string;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY: FormValues = {
  personId: "", debit: 0, period: "",
  datePaid: todayISO(), paymentType: "form.payment.cash"
};

export function DebitsPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const schema = Yup.object({
    personId: Yup.string().required(t("debits.form.memberRequired")),
    debit: Yup.number().min(0.01).required(t("debits.form.amountRequired")),
    period: Yup.string().required(t("debits.form.periodRequired")),
    datePaid: Yup.string().required(t("debits.form.dateRequired")),
    paymentType: Yup.string()
  });
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Debit[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteDebit, setDeleteDebit] = useState<Debit | null>(null);

  // Filtri: socio + modalità periodo / anno / personalizzato (da data a data).
  // Default = periodo corrente.
  const [socioFilter, setSocioFilter] = useState(searchParams.get("personId") ?? "");
  const [filterMode, setFilterMode] = useState<FilterMode>("period");
  // Periodo iniziale dalla query (arrivo da "Gestisci pagamenti" nel dettaglio ricevuta),
  // altrimenti il periodo corrente.
  const [period, setPeriod] = useState(searchParams.get("period") || currentPeriod());
  const [year, setYear] = useState(`${CURRENT_YEAR}`);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Anagrafica soci (una volta) per risolvere personId → nome.
  useEffect(() => {
    call<{ data: Partner[] }>("/api/v1/partners")
      .then((r) => setPartners(r.data ?? []))
      .catch(() => setPartners([]));
  }, [call]);

  // Pagamenti filtrati lato server (endpoint /history: period | year | dateFrom/dateTo + personId).
  const fetchDebits = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filterMode === "period" && period) qs.set("period", period);
    else if (filterMode === "year" && year) qs.set("year", year);
    else if (filterMode === "custom") {
      if (dateFrom) qs.set("dateFrom", dateFrom);
      if (dateTo) qs.set("dateTo", dateTo);
    }
    if (socioFilter) qs.set("personId", socioFilter);
    call<{ data: Debit[] } | Debit[]>(`/api/v1/payments/debits/history?${qs.toString()}`)
      .then((v) => setRows(Array.isArray(v) ? v : (v.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call, filterMode, period, year, dateFrom, dateTo, socioFilter]);

  useEffect(() => { fetchDebits(); }, [fetchDebits]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const partner = partners.find((p) => p._id === values.personId);
        const body: Record<string, unknown> = {
          personId: values.personId,
          debit: values.debit,
          period: values.period,
          datePaid: values.datePaid,
          paymentType: values.paymentType,
          partnerType: partner?.partnerType ?? "form.partnerType.other"
        };
        if (values._id) {
          // In modifica NON si tocca il numero ricevuta (lo si lascia invariato).
          await call(`/api/v1/payments/debits/${values._id}`, { method: "PATCH", body });
          ui.toast(t("common.saved"), "success");
        } else {
          await call("/api/v1/payments/debits", { method: "POST", body: { ...body, paymentNumber: "NEW" } });
        }
        setDialogOpen(false); fetchDebits();
      } catch {
        ui.toast(t("common.error"), "error");
      } finally { setSaving(false); }
    }
  });

  const openEdit = (d: Debit) => {
    setEditing(true);
    formik.resetForm({
      values: {
        _id: d._id,
        personId: d.personId,
        debit: d.debit,
        period: d.period,
        datePaid: d.datePaid ? format(new Date(d.datePaid), "yyyy-MM-dd") : todayISO(),
        paymentType: d.paymentType ?? "form.payment.cash"
      }
    });
    setDialogOpen(true);
  };

  const handleDeleteDebit = useCallback(async () => {
    if (!deleteDebit?._id) return;
    try {
      await call(`/api/v1/payments/debits/${deleteDebit._id}`, { method: "DELETE" });
      ui.toast(t("common.deleted"), "success");
    } catch {
      ui.toast(t("common.error"), "error");
    }
    setDeleteDebit(null);
    fetchDebits();
  }, [deleteDebit, call, t, fetchDebits]);

  const buildActions = (d: Debit) => [
    { key: "edit", label: t("common.edit"), description: t("debits.register"), icon: <EditIcon fontSize="small" />, onClick: () => openEdit(d) },
    { key: "del", label: t("common.delete"), description: t("debits.deleteConfirm", { name: partnerName(d.personId) }), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteDebit(d) }
  ];

  const partnerName = (id: string) => {
    const p = partners.find(p => p._id === id);
    return p ? `${p.lastName} ${p.firstName}` : id;
  };

  const filtered = rows
    .filter(r =>
      `${partnerName(r.personId)} ${r.paymentNumber ?? ""}`.toLowerCase().includes(search.toLowerCase())
    )
    // Dal più recente al più vecchio: per data di pagamento, fallback su data di inserimento.
    .sort((a, b) => {
      const ta = a.datePaid ? new Date(a.datePaid).getTime() : 0;
      const tb = b.datePaid ? new Date(b.datePaid).getTime() : 0;
      if (tb !== ta) return tb - ta;
      const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return cb - ca || String(b._id ?? "").localeCompare(String(a._id ?? ""));
    });

  const total = filtered.reduce((s, r) => s + r.debit, 0);

  const columns: GridColDef[] = [
    {
      field: "personId", headerName: t("debits.socio"), flex: 1, minWidth: 180,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Typography variant="body2" fontWeight={600}>{partnerName(params.row.personId)}</Typography>
      )
    },
    {
      field: "period", headerName: t("common.period"), width: 110,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Chip size="small" label={params.row.period} variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "datePaid", headerName: t("debits.dateCol"), width: 140,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Box>
          <Typography variant="body2">{params.row.datePaid ? format(new Date(params.row.datePaid), "dd/MM/yyyy") : "—"}</Typography>
          {params.row.createdAt && (
            <Typography variant="caption" color="text.secondary" component="div">
              {format(new Date(params.row.createdAt), "HH:mm")}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: "debit", headerName: t("common.amount"), width: 120,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Typography variant="body2" fontWeight={700} color="success.dark">
          € {params.row.debit.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: "paymentType", headerName: t("debits.method"), width: 150,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Chip size="small" label={params.row.paymentType ? formLabel(params.row.paymentType) : "—"} color="default" variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "paymentNumber", headerName: t("debits.receiptNumber"), width: 130,
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <Typography variant="caption" color="text.secondary" noWrap>
          {params.row.paymentNumber ?? "—"}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false, align: "right", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Debit>) => (
        <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildActions(params.row)} />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("debits.title")} subtitle={t("debits.subtitle")}
        onRefresh={fetchDebits}
        actionLabel={t("debits.register")} onAction={() => {
          setEditing(false);
          formik.resetForm({ values: { ...EMPTY, period: filterMode === "period" ? period : currentPeriod(), datePaid: todayISO() } });
          setDialogOpen(true);
        }} />
      <Box sx={{ p: 3, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Riga 1: socio + ricerca + totale */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
              <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }} data-tour="debits-socio">
                <ComboPartners label={t("debits.socio")} value={socioFilter} onChange={setSocioFilter} />
              </Box>
              <TextField size="small" placeholder={t("debits.searchPlaceholder")}
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
                sx={{ width: { xs: "100%", md: 240 } }}
              />
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">{t("common.total")}:</Typography>
                <Typography variant="subtitle2" fontWeight={700} color="success.dark">
                  € {total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                </Typography>
              </Stack>
            </Stack>
            {/* Riga 2: modalità filtro temporale */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap" data-tour="debits-filters">
              <ToggleButtonGroup exclusive size="small" color="primary" value={filterMode}
                onChange={(_, v: FilterMode | null) => v && setFilterMode(v)}>
                <ToggleButton value="period">{t("debits.filterPeriod")}</ToggleButton>
                <ToggleButton value="year">{t("debits.filterYear")}</ToggleButton>
                <ToggleButton value="custom">{t("debits.filterCustom")}</ToggleButton>
              </ToggleButtonGroup>

              {filterMode === "period" && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("common.period")}</InputLabel>
                  <Select label={t("common.period")} value={period} onChange={(e) => setPeriod(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                    {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {filterMode === "year" && (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("common.year")}</InputLabel>
                  <Select label={t("common.year")} value={year} onChange={(e) => setYear(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                    {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {filterMode === "custom" && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField size="small" type="date" label={t("debits.dateFrom")} InputLabelProps={{ shrink: true }}
                    value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  <TextField size="small" type="date" label={t("debits.dateTo")} InputLabelProps={{ shrink: true }}
                    value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </Stack>
              )}
            </Stack>
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("debits.none")} description={t("debits.noneDesc")} actionLabel={t("debits.register")} onAction={() => setDialogOpen(true)} />
            : isMobile
              ? (
                <Box>
                  {filtered.map((r, idx) => (
                    <Box key={r._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
                          {partnerName(r.personId)}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                          <Chip size="small" label={r.period} variant="outlined" sx={{ fontSize: "0.72rem" }} />
                          <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildActions(r)} />
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700} color="success.dark">
                          € {r.debit.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </Typography>
                        <Chip size="small" variant="outlined"
                          label={r.paymentType ? formLabel(r.paymentType) : "—"} sx={{ fontSize: "0.72rem" }} />
                        <Typography variant="caption" color="text.secondary">
                          {r.datePaid ? format(new Date(r.datePaid), "dd/MM/yyyy") : "—"}{r.createdAt ? ` · ${format(new Date(r.createdAt), "HH:mm")}` : ""}
                        </Typography>
                      </Stack>
                      {r.paymentNumber && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {t("debits.receiptNumber")}: {r.paymentNumber}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={56} pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                    "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                    "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                    "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
                  }}
                />
          }
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editing ? t("common.edit") : t("debits.register")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.debits") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("debits.form.memberLabel")}</InputLabel>
                  <Select label={t("debits.form.memberLabel")} {...formik.getFieldProps("personId")}
                    error={formik.touched.personId && Boolean(formik.errors.personId)}>
                    {partners.map(p => <MenuItem key={p._id} value={p._id}>{p.lastName} {p.firstName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label={t("debits.form.amountLabel")} type="number"
                  {...formik.getFieldProps("debit")} inputProps={{ step: 0.01, min: 0 }}
                  error={formik.touched.debit && Boolean(formik.errors.debit)}
                  helperText={formik.touched.debit && formik.errors.debit}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label={t("debits.form.periodLabel")}
                  {...formik.getFieldProps("period")}
                  error={formik.touched.period && Boolean(formik.errors.period)}
                  helperText={formik.touched.period && formik.errors.period}
                  placeholder={t("debits.form.periodPlaceholder")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label={t("debits.form.dateLabel")} type="date"
                  InputLabelProps={{ shrink: true }}
                  {...formik.getFieldProps("datePaid")}
                  error={formik.touched.datePaid && Boolean(formik.errors.datePaid)}
                  helperText={formik.touched.datePaid && formik.errors.datePaid}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("debits.form.methodLabel")}</InputLabel>
                  <Select label={t("debits.form.methodLabel")} {...formik.getFieldProps("paymentType")}>
                    {PAYMENT_TYPES.map(m => <MenuItem key={m} value={m}>{formLabel(m)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : t("debits.form.submit")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteDebit)}
        message={t("debits.deleteConfirm", { name: deleteDebit ? partnerName(deleteDebit.personId) : "" })}
        onConfirm={handleDeleteDebit}
        onCancel={() => setDeleteDebit(null)}
      />
    </Box>
  );
}
